// Акции площадки и заявки продавцов на участие (П3, fd-promo-32). Типы,
// http-реализация и мок живут здесь, а не в общих types.ts/http.ts: в `Api`
// это пространство `api.promoCampaigns.*`.

export type PlatformPromoDiscountType = "percent" | "amount";

export type PlatformPromoCampaign = {
	id: string;
	title: string;
	description: string;
	conditions: string;
	discountType: PlatformPromoDiscountType;
	discountValue: number;
	startsAt: string;
	endsAt: string;
	accepting: boolean;
	isPublic: boolean;
	enabled: boolean;
	state: "scheduled" | "active" | "finished" | "paused";
	productCount: number;
	pendingCount: number;
	createdAt: string;
	updatedAt: string;
};

export type PlatformPromoCampaignInput = {
	title: string;
	description?: string;
	conditions?: string;
	discountType: PlatformPromoDiscountType;
	discountValue: number;
	startsAt: string;
	endsAt: string;
	accepting?: boolean;
	isPublic?: boolean;
	enabled?: boolean;
};

export type PlatformPromoCampaignItem = {
	productId: string;
	name: string;
	photo: string | null;
	price: number;
	shopId: string;
	shopName: string;
};

export type PromoApplicationStatus = "pending" | "approved" | "rejected" | "withdrawn";

export type AdminPromoApplication = {
	id: string;
	shopId: string;
	shopName: string;
	targetKind: "platform_campaign" | "collaboration";
	targetId: string;
	targetTitle: string;
	targetStartsAt: string | null;
	targetEndsAt: string | null;
	productIds: string[];
	products: { id: string; name: string; photo: string | null; price: number }[];
	comment: string;
	status: PromoApplicationStatus;
	reason: string | null;
	reviewedBy: string | null;
	reviewedAt: string | null;
	createdAt: string;
};

export type PromoCampaignsApi = {
	list(): Promise<PlatformPromoCampaign[]>;
	create(input: PlatformPromoCampaignInput): Promise<PlatformPromoCampaign>;
	update(id: string, patch: Partial<PlatformPromoCampaignInput>): Promise<PlatformPromoCampaign>;
	items(id: string): Promise<PlatformPromoCampaignItem[]>;
	removeItem(id: string, productId: string): Promise<void>;
	applications(filter?: { status?: string; targetId?: string }): Promise<AdminPromoApplication[]>;
	review(id: string, decision: { status: "approved" | "rejected"; reason?: string | null }): Promise<AdminPromoApplication>;
};

type Requester = <T>(
	path: string,
	opts?: { method?: string; query?: Record<string, string | number | undefined>; body?: unknown },
) => Promise<T>;

const enc = encodeURIComponent;

export const createPromoCampaignsHttp = (request: Requester): PromoCampaignsApi => ({
	list: () => request<PlatformPromoCampaign[]>("/admin/platform-promo-campaigns"),
	create: (input) => request<PlatformPromoCampaign>("/admin/platform-promo-campaigns", { method: "POST", body: input }),
	update: (id, patch) =>
		request<PlatformPromoCampaign>(`/admin/platform-promo-campaigns/${enc(id)}`, { method: "PATCH", body: patch }),
	items: (id) => request<PlatformPromoCampaignItem[]>(`/admin/platform-promo-campaigns/${enc(id)}/items`),
	removeItem: async (id, productId) => {
		await request(`/admin/platform-promo-campaigns/${enc(id)}/items/${enc(productId)}`, { method: "DELETE" });
	},
	applications: (filter = {}) =>
		request<AdminPromoApplication[]>("/admin/promo-applications", {
			query: { status: filter.status || undefined, targetId: filter.targetId || undefined },
		}),
	review: (id, decision) =>
		request<AdminPromoApplication>(`/admin/promo-applications/${enc(id)}`, { method: "PATCH", body: decision }),
});

// ── Мок: консоль поднимается без шлюза ─────────────────────────────────────
const shift = (days: number) => new Date(Date.now() + days * 86_400_000).toISOString();
let mockCampaigns: PlatformPromoCampaign[] = [
	{
		id: "ppc-black-friday",
		title: "Чёрная пятница OSMIX",
		description: "Большая распродажа площадки",
		conditions: "Скидка не меньше 15 %, товар в наличии",
		discountType: "percent",
		discountValue: 20,
		startsAt: shift(20),
		endsAt: shift(24),
		accepting: true,
		isPublic: true,
		enabled: true,
		state: "scheduled",
		productCount: 0,
		pendingCount: 1,
		createdAt: shift(-2),
		updatedAt: shift(-2),
	},
];
let mockApplications: AdminPromoApplication[] = [
	{
		id: "pap-demo",
		shopId: "shop-handmade",
		shopName: "Мастерская Анны",
		targetKind: "platform_campaign",
		targetId: "ppc-black-friday",
		targetTitle: "Чёрная пятница OSMIX",
		targetStartsAt: shift(20),
		targetEndsAt: shift(24),
		productIds: ["p-1", "p-2", "p-3", "p-4", "p-5"],
		products: [1, 2, 3, 4, 5].map((n) => ({ id: `p-${n}`, name: `Товар ${n}`, photo: null, price: 1500 * n })),
		comment: "Участвуем осенней коллекцией",
		status: "pending",
		reason: null,
		reviewedBy: null,
		reviewedAt: null,
		createdAt: shift(-1),
	},
];

export const mockPromoCampaigns: PromoCampaignsApi = {
	list: async () => mockCampaigns,
	create: async (input) => {
		const campaign: PlatformPromoCampaign = {
			id: `ppc-${Date.now()}`,
			description: "",
			conditions: "",
			accepting: true,
			isPublic: true,
			enabled: true,
			...input,
			state: Date.parse(input.startsAt) > Date.now() ? "scheduled" : "active",
			productCount: 0,
			pendingCount: 0,
			createdAt: new Date().toISOString(),
			updatedAt: new Date().toISOString(),
		};
		mockCampaigns = [campaign, ...mockCampaigns];
		return campaign;
	},
	update: async (id, patch) => {
		const found = mockCampaigns.find((c) => c.id === id);
		if (!found) throw new Error("Акция площадки не найдена");
		Object.assign(found, patch, { updatedAt: new Date().toISOString() });
		return found;
	},
	items: async () => [],
	removeItem: async () => {},
	applications: async (filter = {}) =>
		mockApplications.filter((a) => !filter.status || filter.status === "all" || a.status === filter.status),
	review: async (id, decision) => {
		const found = mockApplications.find((a) => a.id === id);
		if (!found) throw new Error("Заявка не найдена");
		if (decision.status === "rejected" && !decision.reason?.trim()) throw new Error("Укажите причину отказа");
		found.status = decision.status;
		found.reason = decision.status === "rejected" ? (decision.reason ?? null) : null;
		found.reviewedAt = new Date().toISOString();
		mockApplications = [...mockApplications];
		return found;
	},
};
